import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartPulse, Shield, Clock, Users, Award, Heart, Activity, Bandage, AlertTriangle, Truck, Star, ChevronDown, ArrowRight, MapPin, CheckCircle, Home, Building, Play, BookOpen, GraduationCap, Monitor, HandMetal, Check } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { HeroTrustBar } from '../components/HeroTrustBar';
import { InstructorAuthority } from '../components/InstructorAuthority';
// --- Data ---
const skills = [{
  icon: HeartPulse,
  title: 'Pet CPR',
  desc: 'Learn life-saving chest compressions and rescue breathing for dogs and cats.'
}, {
  icon: AlertTriangle,
  title: 'Choking Response',
  desc: 'Safely clear airway obstructions and perform the Heimlich maneuver for pets.'
}, {
  icon: Bandage,
  title: 'Wound Care',
  desc: 'Manage bleeding, apply proper bandages, and prevent infection before reaching the vet.'
}, {
  icon: Shield,
  title: 'Poison Response',
  desc: 'Identify common toxins and know exactly what to do (and what not to do) if ingested.'
}, {
  icon: Activity,
  title: 'Seizure Management',
  desc: "Keep your pet safe during a seizure and know when it's a critical emergency."
}, {
  icon: Truck,
  title: 'Emergency Transport',
  desc: 'Safely move an injured or immobile pet to the emergency clinic without causing further harm.'
}];
const testimonials = [{
  quote: 'The PawVerse instructor came right to our home and trained our whole family. Two months later, my golden retriever started choking on a toy. Because of that training, I knew exactly what to do. I saved his life.',
  name: 'Sarah Jenkins',
  role: 'Pet Parent, San Jose',
  rating: 5
}, {
  quote: 'We had PawVerse come to our doggy daycare for a group session. The training was incredibly hands-on, professional, and tailored to our specific environment. Our staff feels so much more confident now.',
  name: 'Marcus Chen',
  role: 'Owner, Happy Paws Daycare, Oakland',
  rating: 5
}, {
  quote: "As a professional dog walker, this certification gives my clients so much peace of mind. The on-site format made it easy to schedule around my work. The instructor's real-world experience really shows.",
  name: 'Elena Rodriguez',
  role: 'Professional Dog Walker, San Francisco',
  rating: 5
}];
const faqs = [{
  q: 'Do I get a certification after the training?',
  a: 'Yes! Upon successful completion, you will receive a Pet CPR & First Aid Certificate valid for two years, perfect for pet professionals and businesses.'
}, {
  q: 'Where does the training take place?',
  a: 'All training is delivered on-site at your location. We travel to homes, pet businesses, offices, community spaces, and pet facilities throughout the Bay Area. We bring all equipment and materials to you.'
}, {
  q: 'How long is the training session?',
  a: 'Our comprehensive Pet CPR & First Aid course runs for approximately 4 hours, including a short break. We cover both theory and extensive hands-on practice.'
}, {
  q: 'Do you travel anywhere in the Bay Area?',
  a: 'Yes. We provide on-site training throughout San Jose, San Francisco, Oakland, Palo Alto, Mountain View, Sunnyvale, Santa Clara, Cupertino, and surrounding Bay Area communities.'
}, {
  q: 'What is the minimum group size for on-site training?',
  a: 'We can accommodate private sessions for individuals and families as well as larger groups for businesses and organizations. Contact us to discuss your specific needs.'
}];
// --- Components ---
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-warm-gray-200 py-5" data-id="element-355">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left group" aria-expanded={isOpen} data-id="element-356">
        <span className="font-heading text-xl text-primary-900 group-hover:text-accent-500 transition-colors pr-8" data-id="element-357">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-accent-500 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-358" />
      </button>
      <AnimatePresence data-id="element-359">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-360">
            <p className="pt-4 pb-2 text-warm-gray-700 font-body leading-relaxed" data-id="element-361">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function HomePage() {
  return <div className="w-full" data-id="element-362">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-48 overflow-hidden" data-id="element-363">
        <div className="absolute inset-0 z-0" data-id="element-364">
          <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200" alt="Golden Retriever looking attentively" className="w-full h-full object-cover object-center" data-id="element-365" />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-white via-warm-white/90 to-warm-white/40" data-id="element-366"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-367">
          <div className="max-w-2xl" data-id="element-368">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-50 text-accent-700 font-body text-sm font-semibold mb-6 shadow-warm-sm" data-id="element-369">
              <GraduationCap className="w-4 h-4" data-id="element-370" />
              <span data-id="element-371">Free → Certified — Learn at Your Own Pace</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-4xl lg:text-6xl font-heading text-primary-900 leading-tight mb-6" data-id="element-372">
              Every Pet Owner Should Know This.{' '}
              <span className="text-accent-500" data-id="element-373">Most Don't.</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg lg:text-xl text-warm-gray-700 font-body mb-10 leading-relaxed" data-id="element-374">
              1 in 4 pets face emergencies, and most owners freeze. PawVerse
              gives you a clear path from free tips to full certification.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-375">
              <Link to="/resources" className="inline-flex justify-center items-center px-8 py-4 border-2 border-primary-800 text-base font-medium rounded-full text-primary-800 bg-transparent hover:bg-primary-50 transition-all" data-id="element-376">
                Start Learning Free
              </Link>
              <Link to="/programs" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-base font-medium rounded-full text-warm-white bg-primary-800 hover:bg-primary-700 shadow-warm hover:shadow-warm-lg transition-all hover:-translate-y-0.5" data-id="element-377">
                Get Certified
              </Link>
            </motion.div>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="mt-8 text-sm font-body text-warm-gray-500 leading-relaxed" data-id="element-378">
              Serving San Jose, San Francisco, Oakland, Palo Alto, Mountain
              View, Sunnyvale and surrounding Bay Area communities.
            </motion.p>
          </div>
        </div>
      </section>

      {/* 1.5 HERO TRUST BAR */}
      <HeroTrustBar data-id="element-379" />

      {/* 2. WHY THIS MATTERS */}
      <AnimatedSection className="py-24 bg-primary-900 text-warm-white" data-id="element-380">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-381">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-382">
            <h2 className="text-3xl lg:text-4xl font-heading mb-6" data-id="element-383">
              Pet Emergencies Don't Wait. Neither Should You.
            </h2>
            <p className="text-primary-100 font-body text-lg leading-relaxed" data-id="element-384">
              We love our pets like family, but very few of us are prepared to
              save them. The average response time for an emergency vet visit in
              the Bay Area is over 30 minutes. In critical moments, you are
              their best hope.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-385">
            {[{
            stat: '1 in 4',
            text: 'More pets could be saved if just one pet first aid technique was applied prior to getting veterinary care.'
          }, {
            stat: '60%',
            text: 'Of animal hospital visits are emergency in nature where immediate stabilization is required.'
          }, {
            stat: '10 Min',
            text: 'The critical window for oxygen deprivation during cardiac arrest or choking before irreversible damage occurs.'
          }].map((item, i) => <div key={i} className="bg-primary-800 rounded-2xl p-8 text-center shadow-warm-lg border border-primary-700" data-id="element-386">
                <div className="text-4xl font-heading text-accent-400 mb-4" data-id="element-387">
                  {item.stat}
                </div>
                <p className="font-body text-primary-50 leading-relaxed" data-id="element-388">
                  {item.text}
                </p>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 3. HOW IT WORKS - The Learning Journey */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-389">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-390">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-391">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-392">
              A Clear Path From Awareness to Certification
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-393">
              Learn at your own pace. Start free. Go as deep as you want.
            </p>
          </div>

          <div className="relative" data-id="element-394">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-accent-200 z-0" data-id="element-395"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 relative z-10" data-id="element-396">
              {[{
              icon: BookOpen,
              step: '01',
              title: 'Discover',
              desc: 'Free videos, tips, and guides'
            }, {
              icon: Monitor,
              step: '02',
              title: 'Learn',
              desc: 'Structured video courses with quizzes'
            }, {
              icon: HandMetal,
              step: '03',
              title: 'Practice',
              desc: 'Hands-on workshops in the Bay Area'
            }, {
              icon: Award,
              step: '04',
              title: 'Certify',
              desc: 'Complete certification pathway'
            }].map((stage, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-397">
                  <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mb-6 border-4 border-warm-white shadow-warm relative" data-id="element-398">
                    <stage.icon className="w-8 h-8 text-primary-800" data-id="element-399" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent-500 text-primary-900 font-bold font-body text-sm flex items-center justify-center border-2 border-warm-white" data-id="element-400">
                      {stage.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-heading text-primary-900 mb-2" data-id="element-401">
                    {stage.title}
                  </h3>
                  <p className="text-warm-gray-600 font-body" data-id="element-402">{stage.desc}</p>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 4. LEARNING PATHS - Product Tier Cards */}
      <AnimatedSection className="py-24 bg-warm-gray-50" data-id="element-403">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-404">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-405">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-406">
              Choose Your Learning Path
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-407">
              Whether you want quick tips or full certification, there's a path
              for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch" data-id="element-408">
            {/* Tier 1: Free */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-409">
              <div className="mb-6" data-id="element-410">
                <span className="inline-block px-3 py-1 rounded-full bg-warm-gray-100 text-warm-gray-700 font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-411">
                  Free
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-412">
                  Pet Safety Basics
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[60px]" data-id="element-413">
                  Short videos, emergency checklists, and practical tips to get
                  started.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-414">
                Free{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-415">
                  forever
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-416">
                {['5-minute video lessons', 'Emergency quick-reference guides', 'Pet poison awareness guide', 'Community access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-417">
                    <Check className="w-5 h-5 text-accent-500 shrink-0" data-id="element-418" />
                    <span data-id="element-419">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/resources" className="w-full py-3 px-4 flex items-center justify-center gap-2 text-primary-800 font-bold font-body hover:text-accent-600 transition-colors mt-auto" data-id="element-420">
                Start Free <ArrowRight className="w-4 h-4" data-id="element-421" />
              </Link>
            </div>

            {/* Tier 2: Paid Video Course */}
            <div className="bg-white rounded-2xl p-8 shadow-warm-lg border-2 border-accent-300 flex flex-col relative transform lg:-translate-y-4" data-id="element-422">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-accent-500 text-primary-900 px-4 py-1 rounded-full text-xs font-bold font-body uppercase tracking-wider" data-id="element-423">
                Most Popular
              </div>
              <div className="mb-6 mt-2" data-id="element-424">
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-425">
                  Complete Video Course
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[60px]" data-id="element-426">
                  Comprehensive self-paced video training covering all core pet
                  emergency skills.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-427">
                $89{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-428">
                  lifetime access
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-429">
                {['Full CPR & First Aid curriculum', 'HD video demonstrations', 'Knowledge checks & quizzes', 'Certificate of completion', 'Lifetime access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-430">
                    <Check className="w-5 h-5 text-accent-500 shrink-0" data-id="element-431" />
                    <span data-id="element-432">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/video-course" className="w-full py-3 px-4 text-center border-2 border-primary-800 text-primary-800 rounded-xl font-bold font-body hover:bg-primary-50 transition-colors mt-auto" data-id="element-433">
                Enroll Now
              </Link>
            </div>

            {/* Tier 3: In-Person Workshop */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-434">
              <div className="mb-6" data-id="element-435">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-800 font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-436">
                  Bay Area
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-437">
                  Hands-On Workshop
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[60px]" data-id="element-438">
                  In-person training at your location with practice on
                  professional mannequins.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-439">
                $199{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-440">
                  / group
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-441">
                {['Everything in Video Course', 'Hands-on practice with mannequins', 'Real-time instructor feedback', 'Custom scenario training', 'We come to your location'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-442">
                    <Check className="w-5 h-5 text-accent-500 shrink-0" data-id="element-443" />
                    <span data-id="element-444">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/workshops" className="w-full py-3 px-4 text-center bg-primary-800 text-warm-white rounded-xl font-bold font-body hover:bg-primary-700 transition-colors mt-auto" data-id="element-445">
                Request Workshop
              </Link>
            </div>

            {/* Tier 4: Certification */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-446">
              <div className="mb-6" data-id="element-447">
                <span className="inline-block px-3 py-1 rounded-full bg-warm-gray-800 text-warm-white font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-448">
                  Professional
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-449">
                  Full Certification
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[60px]" data-id="element-450">
                  Complete online + in-person pathway. Earn your Pet CPR & First
                  Aid Certification.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-451">
                $299{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-452">
                  starting at
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-453">
                {['Complete Video Course included', 'In-person skills assessment', '2-year certification', 'Digital badge & certificate', 'Continuing education credits'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-454">
                    <Check className="w-5 h-5 text-accent-500 shrink-0" data-id="element-455" />
                    <span data-id="element-456">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/programs" className="w-full py-3 px-4 text-center bg-accent-500 text-primary-900 rounded-xl font-bold font-body hover:bg-accent-400 transition-colors mt-auto" data-id="element-457">
                Get Certified
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 5. WHAT YOU'LL LEARN */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-458">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-459">
          <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-460">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-461">
              Real Skills. Not Theory.
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-462">
              Every module teaches you a specific, actionable skill you can use
              in a real emergency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-463">
            {skills.map((skill, i) => <div key={i} className="bg-white rounded-2xl p-8 shadow-warm hover:shadow-warm-lg transition-shadow border border-warm-gray-100 group" data-id="element-464">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent-50 transition-colors" data-id="element-465">
                  <skill.icon className="w-6 h-6 text-primary-800 group-hover:text-accent-600" data-id="element-466" />
                </div>
                <h3 className="text-xl font-heading text-primary-900 mb-3" data-id="element-467">
                  {skill.title}
                </h3>
                <p className="text-warm-gray-600 font-body leading-relaxed" data-id="element-468">
                  {skill.desc}
                </p>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-469">
            <Link to="/programs" className="inline-flex items-center gap-2 text-primary-800 font-medium font-body hover:text-accent-600 transition-colors" data-id="element-470">
              View full course details <ArrowRight className="w-4 h-4" data-id="element-471" />
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ABOUT THE INSTRUCTOR */}
      <AnimatedSection className="py-24 bg-warm-gray-50" data-id="element-472">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-474">
            <div className="flex flex-col items-center" data-id="element-475">
              <div className="relative max-w-sm mb-8" data-id="element-476">
                <div className="absolute inset-0 bg-accent-100 rounded-[2rem] transform translate-x-3 translate-y-3 opacity-60" data-id="element-477"></div>
                <img src="https://cdn.magicpatterns.com/uploads/tAYd4KeRHKAKf1uGq9kcGM/Screenshot_2026-03-11_at_10.50.47_AM.png" alt="Instructor portrait" className="relative rounded-[2rem] shadow-warm w-full object-cover aspect-[3/4]" data-id="element-478" />
              </div>

              {/* Compact Logo Row */}
            </div>

            <div data-id="element-479">
              <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-480">
                Meet Your Instructor
              </h2>
              <p className="font-body text-lg text-warm-gray-700 leading-relaxed mb-10" data-id="element-481">
                Hi, I'm Jessica — a certified Bay Area pet safety instructor and
                former veterinary technician with 10+ years of emergency clinic
                experience. I bring hands-on pet CPR & first aid training
                directly to your location.
              </p>

              {/* Certified Instructor Credentials */}
              <div className="mb-8 space-y-5" data-id="element-482">
                <span className="font-heading text-sm text-warm-gray-500 uppercase tracking-wider block" data-id="element-483">
                  Certifications & Credentials
                </span>

                <div className="flex items-center gap-4" data-id="element-484">
                  <img src="https://cdn.magicpatterns.com/uploads/35xgbT5p2nGsYFAwLkai8J/image.png" alt="American Heart Association" className="h-12 w-12 object-contain mix-blend-multiply shrink-0" data-id="element-485" />
                  <div data-id="element-486">
                    <p className="font-heading text-base text-primary-900" data-id="element-487">
                      First Aid CPR/ Basic Life Support Instructor
                    </p>
                    <p className="font-body text-sm text-warm-gray-500" data-id="element-488">
                      American Heart Association
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-489">
                  <img src="https://cdn.magicpatterns.com/uploads/mrSVrbfHzpVzeT8P1raWtn/image.png" alt="American Red Cross" className="h-12 w-12 object-contain mix-blend-multiply shrink-0" data-id="element-490" />
                  <div data-id="element-491">
                    <p className="font-heading text-base text-primary-900" data-id="element-492">
                      First Aid CPR/ Basic Life Support Instructor
                    </p>
                    <p className="font-body text-sm text-warm-gray-500" data-id="element-493">
                      American Red Cross
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-494">
                  <img src="https://cdn.magicpatterns.com/uploads/9TL2sE6UjbtKkYy2yH2ywD/PT-Certified-Sticker.png" alt="PetTech PetSaver Certified" className="h-12 w-12 object-contain mix-blend-multiply shrink-0" data-id="element-495" />
                  <div data-id="element-496">
                    <p className="font-heading text-base text-primary-900" data-id="element-497">
                      Pet CPR & First Aid Instructor
                    </p>
                    <p className="font-body text-sm text-warm-gray-500" data-id="element-498">
                      PetTech
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8" data-id="element-499">
                <Link to="/about" className="inline-flex items-center gap-2 text-primary-800 font-medium font-body hover:text-accent-600 transition-colors" data-id="element-500">
                  Read my full story <ArrowRight className="w-4 h-4" data-id="element-501" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. LOCAL TRAINING */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-502">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-503">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-504">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-505">
              In-Person Training Across the Bay Area
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-506">
              Workshops and certification sessions delivered at your location.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 max-w-3xl mx-auto mb-12" data-id="element-507">
            {['San Jose', 'San Francisco', 'Oakland', 'Palo Alto', 'Mountain View', 'Sunnyvale', 'Santa Clara', 'Cupertino'].map((city, i) => <div key={i} className="bg-primary-50 p-4 rounded-xl text-center border border-primary-100" data-id="element-508">
                <MapPin className="w-5 h-5 text-primary-700 mx-auto mb-2" data-id="element-509" />
                <span className="font-body text-primary-900 font-medium text-sm" data-id="element-510">
                  {city}
                </span>
              </div>)}
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-12" data-id="element-511">
            {['All equipment provided', 'Flexible scheduling', 'Homes, businesses & organizations', 'Custom scenarios for your environment'].map((point, i) => <div key={i} className="flex items-center gap-2 text-warm-gray-700 font-body" data-id="element-512">
                <CheckCircle className="w-5 h-5 text-accent-500 shrink-0" data-id="element-513" />
                <span data-id="element-514">{point}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-515">
            <Link to="/workshops" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-base font-medium rounded-full text-warm-white bg-primary-800 hover:bg-primary-700 shadow-warm transition-all hover:-translate-y-0.5" data-id="element-516">
              Schedule On-Site Training
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* 8. TESTIMONIALS */}
      <AnimatedSection className="py-24 bg-warm-gray-50" data-id="element-517">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-518">
          <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-519">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-520">
              Trusted by Bay Area Pet Lovers
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-521">
              Don't just take our word for it. Here's what our trained students
              have to say.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-522">
            {testimonials.map((test, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-warm border border-warm-gray-100 flex flex-col h-full" data-id="element-523">
                <div className="flex gap-1 mb-6" data-id="element-524">
                  {[...Array(test.rating)].map((_, j) => <Star key={j} className="w-5 h-5 fill-accent-500 text-accent-500" data-id="element-525" />)}
                </div>
                <p className="font-body text-warm-gray-700 italic mb-8 flex-grow" data-id="element-526">
                  "{test.quote}"
                </p>
                <div data-id="element-527">
                  <p className="font-heading text-lg text-primary-900" data-id="element-528">
                    {test.name}
                  </p>
                  <p className="font-body text-sm text-warm-gray-500" data-id="element-529">
                    {test.role}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 9. FINAL CTA */}
      <AnimatedSection className="py-24 bg-primary-800 relative overflow-hidden" data-id="element-530">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-531"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-532">
          <h2 className="text-4xl lg:text-5xl font-heading text-warm-white mb-6" data-id="element-533">
            Start With a Free Lesson. End With a Certification.
          </h2>
          <p className="text-xl text-primary-100 font-body mb-10 max-w-2xl mx-auto" data-id="element-534">
            Join thousands of Bay Area pet owners who are learning to save
            lives. Begin your journey today — it's free.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-535">
            <Link to="/resources" className="inline-flex justify-center items-center px-8 py-4 border-2 border-warm-white text-lg font-bold rounded-full text-warm-white bg-transparent hover:bg-white/10 transition-all" data-id="element-536">
              Start Learning Free
            </Link>
            <Link to="/programs" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-lg font-bold rounded-full text-primary-900 bg-accent-500 hover:bg-accent-400 shadow-warm-lg transition-all hover:-translate-y-1" data-id="element-537">
              Get Certified
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}